import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import * as echarts from 'echarts'
import { Select, Checkbox, Dropdown, Menu, Button, Icon, Tooltip } from 'antd'
import refuelIcon from 'assets/images/icon/refuel.png'
import theftIcon from 'assets/images/icon/theft.png'
import dataGapIcon from 'assets/images/icon/data-gap.png'
import './style.css'

const { Option } = Select

// ---------------------------------------------------------------------------
// Grouping options (kept identical to the amCharts version)
// ---------------------------------------------------------------------------
const defaultGroupingItems = [
    { label: 'No Grouping', value: 0 },
    { label: '5 minutes', value: 5 },
    { label: '15 minutes', value: 15 },
    { label: '30 minutes', value: 30 },
    { label: '1 hour', value: 60 },
    { label: '1.5 hours', value: 90 },
    { label: '2 hours', value: 120 },
    { label: '5 hours', value: 300 }
]

const smallGroupingItems = [
    { label: 'No Grouping', value: 0 },
    { label: '1 minutes', value: 1 },
    { label: '3 minutes', value: 3 },
    { label: '5 minutes', value: 5 },
    { label: '10 minutes', value: 10 }
]

const CHART_COLORS = {
    fuel: '#1FAF4B',
    odometer: '#2A9D8F',
    speed: '#457B9D',
    battery: '#F4A261',
    error: '#FF6B6B',
    consumptionRate: '#8338EC',
    rawSensorData: '#FF8C00',
    ignitionState: '#12B886',
    movementState: '#5C7CFA'
}

// Each value series -> its own y-axis. Axis index === series index in this list.
const SERIES_META = [
    { key: 'fuelLevelLiters', name: 'Fuel Level', color: CHART_COLORS.fuel, axisName: 'Fuel Level (ltr.)', side: 'left' },
    { key: 'odometer_kms', name: 'Odometer (km)', color: CHART_COLORS.odometer, axisName: 'Odometer (km)', side: 'right' },
    { key: 'speed_gnss', name: 'Speed', color: CHART_COLORS.speed, axisName: 'Speed (km/h)', side: 'right' },
    { key: 'battery_level', name: 'Battery Level', color: CHART_COLORS.battery, axisName: 'Battery (mV)', side: 'right' },
    { key: 'original_fuel_level_analog', name: 'Raw Fuel Data', color: CHART_COLORS.rawSensorData, axisName: 'Raw Fuel', side: 'right', checkbox: true }
]

const RIGHT_AXIS_STEP = 70

// Default legend visibility: only Fuel Level is on; everything else (incl. bands) off.
const DEFAULT_LEGEND = {
    'Fuel Level': true,
    'Odometer (km)': false,
    Speed: false,
    'Battery Level': false,
    Ignition: false,
    Movement: false
}

const getThemePalette = (dark) => ({
    text: dark ? '#e2e8f0' : '#2f3742',
    muted: dark ? '#c3ccd5' : '#666',
    background: dark ? '#1d2022' : '#ffffff',
    grid: dark ? '#3a444f' : '#e2e8f0',
    axisLine: dark ? '#566576' : '#c9d2db',
    tooltipBg: dark ? 'rgba(29,32,34,0.96)' : 'rgba(255,255,255,0.98)',
    tooltipBorder: dark ? '#3a444f' : '#d8dee5',
    spinnerTrack: dark ? '#4f5c69' : '#f3f3f3',
    spinnerHead: '#3498db'
})

// ---------------------------------------------------------------------------
// Pure data helpers (aggregation + event matching, ported 1:1 in behaviour)
// ---------------------------------------------------------------------------
const getPointTime = (d) => (d.timestamp instanceof Date ? d.timestamp.getTime() : new Date(d.timestamp).getTime())

const aggregateData = (rawData, intervalMinutes, hasFuelSensorAttached) => {
    if (!rawData || rawData.length === 0) return []

    const sorted = [...rawData].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    if (intervalMinutes === 0) return sorted

    const intervalMs = intervalMinutes * 60 * 1000
    const buckets = new Map()

    for (const item of sorted) {
        const timestamp = new Date(item.timestamp)
        const bucketKey = Math.floor(timestamp.getTime() / intervalMs) * intervalMs
        const currentFuelLevel = hasFuelSensorAttached ? item.fuelLevelLiters : item.fuel_level

        if (!buckets.has(bucketKey)) {
            buckets.set(bucketKey, {
                count: 0,
                fuelLevelLiters: 0,
                odometer_kms: 0,
                original_fuel_level_analog: 0,
                speed_gnss: 0,
                timestamp: new Date(bucketKey),
                battery_level: 0,
                totalHours: 0,
                previousTimestamp: null,
                firstFuelLevel: currentFuelLevel,
                lastFuelLevel: currentFuelLevel
            })
        }

        const bucket = buckets.get(bucketKey)
        if (bucket.previousTimestamp) {
            const timeDiff = Number(item.timestamp) - bucket.previousTimestamp
            bucket.totalHours += timeDiff * (Number(item.effective_ignition) || 0)
        }
        bucket.previousTimestamp = Number(item.timestamp)
        bucket.lastFuelLevel = currentFuelLevel

        bucket.count++
        bucket.fuelLevelLiters += currentFuelLevel
        bucket.odometer_kms += item.odometer_kms
        bucket.speed_gnss += item.speed_gnss
        bucket.battery_level += item.bat_vol
        bucket.original_fuel_level_analog += item.original_fuel_level_analog
    }

    return Array.from(buckets.values())
        .map((bucket) => {
            const fuelLevelDifference = Number((bucket.firstFuelLevel - bucket.lastFuelLevel).toFixed(2))
            const totalHours = Number((bucket.totalHours / 1000 / 60 / 60).toFixed(2))
            return {
                timestamp: bucket.timestamp,
                fuelLevelLiters: bucket.count > 0 ? Number((bucket.fuelLevelLiters / bucket.count).toFixed(2)) : 0,
                odometer_kms: bucket.odometer_kms / bucket.count / 1000,
                speed_gnss: bucket.speed_gnss / bucket.count,
                battery_level: bucket.battery_level / bucket.count,
                original_fuel_level_analog: Number(bucket.original_fuel_level_analog / bucket.count).toFixed(2),
                hoursConsumptionRate: totalHours > 0 && isFinite(fuelLevelDifference / totalHours)
                    ? Number((fuelLevelDifference / totalHours).toFixed(2))
                    : 0
            }
        })
        .sort((a, b) => a.timestamp - b.timestamp)
}

const hasErrorEventAtTimestamp = (errorTimeline, timestamp, gi) => {
    const intervalMs = gi * 60 * 1000
    return (errorTimeline || []).some((e) => {
        const errorTime = new Date(e.timestamp).getTime()
        return gi === 0 ? errorTime === timestamp : Math.abs(errorTime - timestamp) <= intervalMs / 2
    })
}

const hasDataGapEventAtTimestamp = (dataGaps, timestamp, gi) => {
    const intervalMs = gi * 60 * 1000
    return (dataGaps || []).some((gap) => {
        const gapTime = gap.to
        return gi === 0 ? gapTime === timestamp : Math.abs(gapTime - timestamp) <= intervalMs / 2
    })
}

const hasRefuelEventAtTimestamp = (consumedData, timestamp, gi) => {
    const intervalMs = gi * 60 * 1000
    const refuelEvents = (consumedData && consumedData.refuel_events) || []
    return refuelEvents.some((r) => {
        const t = r.timestamp || r.startTimestamp
        return gi === 0 ? t === timestamp : Math.abs(t - timestamp) <= intervalMs / 2
    })
}

const hasTheftEventAtTimestamp = (consumedData, timestamp, gi) => {
    const intervalMs = gi * 60 * 1000
    const theftEvents = (consumedData && consumedData.theft_events) || []
    return theftEvents.some((t) => {
        const tt = t.timestamp || t.startTimestamp || t.start_time || t.time || t.date
        if (gi === 0) return tt === timestamp
        return tt >= timestamp && tt < timestamp + intervalMs
    })
}

const findRefuel = (consumedData, timestamp, gi) => {
    const intervalMs = gi * 60 * 1000
    return ((consumedData && consumedData.refuel_events) || []).find((r) => {
        const t = r.timestamp || r.startTimestamp
        return gi === 0 ? t === timestamp : Math.abs(t - timestamp) <= intervalMs / 2
    })
}

const findTheft = (consumedData, timestamp, gi) => {
    const intervalMs = gi * 60 * 1000
    return ((consumedData && consumedData.theft_events) || []).find((t) => {
        const tt = t.timestamp || t.startTimestamp || t.start_time || t.time || t.date
        return gi === 0 ? tt === timestamp : Math.abs(tt - timestamp) <= intervalMs / 2
    })
}

const findGap = (dataGaps, timestamp, gi) => {
    const intervalMs = gi * 60 * 1000
    return (dataGaps || []).find((gap) => (gi === 0 ? gap.to === timestamp : Math.abs(gap.to - timestamp) <= intervalMs / 2))
}

const findError = (errorTimeline, timestamp, gi) => {
    const intervalMs = gi * 60 * 1000
    return (errorTimeline || []).find((e) => {
        const t = new Date(e.timestamp).getTime()
        return gi === 0 ? t === timestamp : Math.abs(t - timestamp) <= intervalMs / 2
    })
}

// Collapse a 0/1 state signal into contiguous "on" intervals.
const buildStateIntervals = (points, isOn) => {
    const intervals = []
    let open = null
    for (const p of points) {
        const t = getPointTime(p)
        if (isOn(p)) {
            if (!open) open = { start: t, end: t }
            else open.end = t
        } else if (open) {
            intervals.push(open)
            open = null
        }
    }
    if (open) intervals.push(open)
    return intervals
}

const mergeIntervals = (intervals, maxGapMs) => {
    if (intervals.length <= 1) return intervals
    const out = [intervals[0]]
    for (let i = 1; i < intervals.length; i++) {
        const prev = out[out.length - 1]
        const cur = intervals[i]
        if (cur.start - prev.end <= maxGapMs) prev.end = cur.end
        else out.push(cur)
    }
    return out
}

const capArray = (arr, cap) => {
    if (arr.length <= cap) return arr
    const stride = arr.length / cap
    const out = []
    for (let i = 0; i < arr.length; i += stride) out.push(arr[Math.floor(i)])
    return out
}

const downloadBlob = (content, type, filename) => {
    const blob = new Blob([content], { type })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}

const toCSV = (rows) => {
    if (!rows || rows.length === 0) return ''
    const keys = Object.keys(rows[rows.length - 1])
    return [
        keys.join(','),
        ...rows.map((item) =>
            keys.map((key) => {
                let value = item[key]
                if (value === undefined || value === null) return ''
                if (value instanceof Date) return value.toISOString()
                if (typeof value === 'number') return Math.abs(value).toFixed(4) * (value < 0 ? -1 : 1)
                if (typeof value === 'string') return value.includes(',') ? `"${value}"` : value
                return value
            }).join(',')
        )
    ].join('\n')
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function RangeDataChartV2({
    data: iotData,
    data_gaps,
    consumedData,
    hasFuelSensorAttached,
    fuelSensorCalibrationList,
    title = 'Data Chart',
    skipInitialLoading = false,
    isDarkMode = false
}) {
    const containerRef = useRef(null)
    const chartRef = useRef(null)
    const buildOptionRef = useRef(null) // always points at the latest buildOption
    // Legend selection is mutable (user toggles) and drives the dynamic axis layout.
    const legendStateRef = useRef({ ...DEFAULT_LEGEND })

    const [isDataReady, setIsDataReady] = useState(false)
    const [isChartLoading, setIsChartLoading] = useState(false)
    const [groupingInterval, setGroupingInterval] = useState(15) // committed value that drives the option build
    const [selectedGrouping, setSelectedGrouping] = useState(15) // immediate value shown in the Select
    const [groupingOptions, setGroupingOptions] = useState(defaultGroupingItems)
    const [needAggregateData, setNeedAggregateData] = useState(true)
    const [showDataGaps, setShowDataGaps] = useState(true)
    const [showRawSensorData, setShowRawSensorData] = useState(false)

    const palette = useMemo(() => getThemePalette(isDarkMode), [isDarkMode])

    // Sentinel fuel-sensor error points, same rule as the original component.
    const errorTimeline = useMemo(() => {
        return (iotData || [])
            .filter((item) => {
                const v = Number(item.original_fuel_level_analog)
                return v === 0 || (v >= 6500 && v <= 7000)
            })
            .map((item) => ({ timestamp: item.timestamp, value: item.original_fuel_level_analog }))
    }, [iotData])

    // Aggregated / raw data for the current grouping.
    const processedData = useMemo(() => {
        if (!iotData || iotData.length === 0) return []
        if (groupingInterval === 0) {
            return [...iotData].sort((a, b) => getPointTime(a) - getPointTime(b))
        }
        return aggregateData(iotData, groupingInterval, hasFuelSensorAttached)
    }, [iotData, groupingInterval, hasFuelSensorAttached])

    // Everything expensive derived once per data/grouping/event change.
    const derived = useMemo(() => {
        if (!processedData.length) {
            return { xy: {}, events: [] }
        }
        const gi = groupingInterval

        // Per-series [x, y] arrays.
        const xy = {}
        SERIES_META.forEach((m) => { xy[m.key] = [] })
        for (const p of processedData) {
            const x = getPointTime(p)
            SERIES_META.forEach((m) => { xy[m.key].push([x, Number(p[m.key])]) })
        }

        // Event markers on the fuel series. Discrete events (refuel/drain/gap) always
        // kept; dense sensor errors are capped so markPoints never explode.
        const iconFor = { refuel: refuelIcon, theft: theftIcon, dataGap: dataGapIcon }
        const discrete = []
        const errors = []
        for (const p of processedData) {
            const x = getPointTime(p)
            const y = Number(p.fuelLevelLiters)
            const active = []
            if (hasRefuelEventAtTimestamp(consumedData, x, gi)) active.push('refuel')
            if (hasTheftEventAtTimestamp(consumedData, x, gi)) active.push('theft')
            if (showDataGaps && hasDataGapEventAtTimestamp(data_gaps, x, gi)) active.push('dataGap')
            const isError = hasErrorEventAtTimestamp(errorTimeline, x, gi)

            active.forEach((type) => {
                const stack = active.indexOf(type) + (isError ? 1 : 0)
                discrete.push({
                    name: type,
                    coord: [x, y],
                    symbol: `image://${iconFor[type]}`,
                    symbolSize: type === 'dataGap' ? 16 : 24,
                    symbolOffset: [0, -(stack * 20) - 12]
                })
            })
            if (isError) {
                errors.push({
                    name: 'error',
                    coord: [x, y],
                    symbol: 'circle',
                    symbolSize: 14,
                    symbolOffset: [0, -12],
                    itemStyle: { color: CHART_COLORS.error }
                })
            }
        }
        const events = discrete.concat(capArray(errors, 500))

        return { xy, events }
    }, [processedData, groupingInterval, consumedData, data_gaps, errorTimeline, showDataGaps])

    // Ignition / movement bands are a state-over-time overlay, independent of the
    // line grouping: compute the on/off intervals once from the RAW data and show
    // the same precise bands at every grouping level. Merging sub-2-min gaps keeps
    // the band count small; capped as a safety net for very noisy signals.
    const stateBands = useMemo(() => {
        if (!iotData || iotData.length === 0) return { ignition: [], movement: [] }
        const sorted = [...iotData].sort((a, b) => getPointTime(a) - getPointTime(b))
        const MERGE = 2 * 60 * 1000
        const CAP = 600
        const ignition = capArray(mergeIntervals(buildStateIntervals(sorted, (p) => {
            const ign = (p.effective_ignition !== undefined && p.effective_ignition !== null) ? p.effective_ignition : p.ignition
            return Number(ign) > 0
        }), MERGE), CAP)
        const movement = capArray(mergeIntervals(buildStateIntervals(sorted, (p) => Number(p.movement) > 0 || Number(p.eye_bea_mov_1) === 1), MERGE), CAP)
        return { ignition, movement }
    }, [iotData])

    // ------------------------------------------------------------------
    // Grouping options + initial grouping, mirroring the original rules.
    // ------------------------------------------------------------------
    useEffect(() => {
        if (iotData && iotData.length <= 4000) {
            setGroupingOptions([
                { label: 'No Grouping', value: 0 },
                { label: '1 minute', value: 1 },
                ...defaultGroupingItems.filter((i) => i.value !== 0)
            ])
        } else {
            setGroupingOptions(defaultGroupingItems)
        }
        if (iotData && iotData.length <= 1000) {
            setNeedAggregateData(false)
            setGroupingInterval(0)
            setSelectedGrouping(0)
        }
        if (iotData && iotData.length > 0) {
            setIsDataReady(true)
            setIsChartLoading(!skipInitialLoading)
        } else {
            setIsDataReady(false)
            setIsChartLoading(false)
        }
    }, [iotData, skipInitialLoading])

    // Commit the picked grouping after paint so the Select stays snappy.
    useEffect(() => {
        if (selectedGrouping === groupingInterval) return
        const raf = requestAnimationFrame(() => {
            requestAnimationFrame(() => setGroupingInterval(selectedGrouping))
        })
        return () => cancelAnimationFrame(raf)
    }, [selectedGrouping, groupingInterval])

    // ------------------------------------------------------------------
    // Build the ECharts option from the derived data.
    // ------------------------------------------------------------------
    const buildOption = useCallback(() => {
        const legendSel = legendStateRef.current
        const gi = groupingInterval

        // Dynamic y-axis layout: only axes for visible series are shown, right-side
        // axes get incremental offsets so the chart stays clean and readable.
        let rightCount = 0
        const yAxis = SERIES_META.map((m) => {
            let visible
            if (m.checkbox) visible = showRawSensorData
            else if (m.name === 'Fuel Level') visible = legendSel['Fuel Level'] !== false
            else visible = legendSel[m.name] === true
            const show = !!visible
            const axis = {
                type: 'value',
                name: m.axisName,
                position: m.side,
                // Titles run vertically along their own axis (centred), so they never
                // collide with each other or with the top-right toolbox icons.
                nameLocation: 'middle',
                nameRotate: 90,
                nameGap: 44,
                nameTextStyle: { color: m.color, fontSize: 10 },
                axisLabel: { color: m.color, fontSize: 10 },
                axisLine: { show: true, lineStyle: { color: m.color } },
                splitLine: { show: m.key === 'fuelLevelLiters', lineStyle: { color: palette.grid } },
                scale: true,
                show
            }
            if (m.side === 'right') {
                axis.offset = show ? rightCount * RIGHT_AXIS_STEP : 0
                if (show) rightCount++
            }
            return axis
        })

        const rightPad = 24 + rightCount * RIGHT_AXIS_STEP
        const fuelVisible = legendSel['Fuel Level'] !== false

        const series = SERIES_META.map((m, idx) => {
            const isFuel = m.key === 'fuelLevelLiters'
            // Raw-sensor line is controlled by its checkbox (not the legend): empty
            // it out when the checkbox is off so it never draws.
            const seriesData = m.checkbox && !showRawSensorData ? [] : (derived.xy[m.key] || [])
            const s = {
                name: m.name,
                type: 'line',
                yAxisIndex: idx,
                data: seriesData,
                showSymbol: false,
                symbol: 'circle',
                symbolSize: 6,
                sampling: 'lttb',
                smooth: false,
                connectNulls: true,
                lineStyle: { width: isFuel ? 1.5 : 1, color: m.color, type: m.dashed ? 'dashed' : 'solid' },
                itemStyle: { color: m.color },
                emphasis: { focus: 'series' },
                animation: false
            }
            if (m.checkbox) {
                // Raw sensor series is controlled by the checkbox, not the legend.
                s.silent = false
            }
            if (isFuel) {
                s.z = 5
                s.markPoint = {
                    silent: false,
                    data: derived.events,
                    tooltip: { show: false }
                }
            }
            return s
        })

        // Ignition / movement band series (empty line + markArea, toggled by legend).
        const makeBandSeries = (name, color, intervals) => ({
            name,
            type: 'line',
            yAxisIndex: 0,
            data: [],
            showSymbol: false,
            silent: true,
            lineStyle: { opacity: 0 },
            markArea: {
                silent: true,
                itemStyle: { color, opacity: 0.16 },
                data: intervals.map((iv) => [{ xAxis: iv.start }, { xAxis: iv.end }])
            }
        })
        series.push(makeBandSeries('Ignition', CHART_COLORS.ignitionState, stateBands.ignition))
        series.push(makeBandSeries('Movement', CHART_COLORS.movementState, stateBands.movement))

        const legendData = SERIES_META.filter((m) => !m.checkbox).map((m) => m.name).concat(['Ignition', 'Movement'])

        return {
            backgroundColor: palette.background,
            animation: false,
            textStyle: { color: palette.text },
            grid: { left: fuelVisible ? 74 : 24, right: rightPad, top: 40, bottom: 92, containLabel: false },
            legend: {
                type: 'scroll',
                data: legendData,
                selected: legendSel,
                textStyle: { color: palette.text },
                inactiveColor: palette.muted,
                top: 6
            },
            tooltip: {
                trigger: 'axis',
                confine: true,
                axisPointer: { type: 'cross', snap: true, lineStyle: { color: palette.muted } },
                backgroundColor: palette.tooltipBg,
                borderColor: palette.tooltipBorder,
                textStyle: { color: palette.text, fontSize: 12 },
                formatter: (params) => {
                    if (!params || !params.length) return ''
                    const x = params[0].value ? params[0].value[0] : params[0].axisValue
                    const ts = new Date(x)
                    let html = `<div style="font-weight:600;margin-bottom:4px">${ts.toLocaleString()}</div>`
                    params.forEach((p) => {
                        if (!p.value || p.value[1] === undefined || p.value[1] === null) return
                        if (p.seriesName === 'Ignition' || p.seriesName === 'Movement') return
                        html += `${p.marker} ${p.seriesName}: <b>${Number(p.value[1]).toFixed(2)}</b><br/>`
                    })
                    // Append event info at this x.
                    const refuel = findRefuel(consumedData, x, gi)
                    if (refuel) html += `<span style="color:${CHART_COLORS.fuel}">⛽ Refuel: <b>${Math.abs(Number(refuel.refuelAmount || refuel.amount || 0)).toFixed(2)} L</b></span><br/>`
                    const theft = findTheft(consumedData, x, gi)
                    if (theft) html += `<span style="color:${CHART_COLORS.error}">⚠ Drain: <b>${Math.abs(Number(theft.theftAmount || theft.amount || 0)).toFixed(2)} L</b></span><br/>`
                    if (showDataGaps) {
                        const gap = findGap(data_gaps, x, gi)
                        if (gap) html += `<span style="color:${palette.muted}">▨ Data Gap: <b>${Math.abs(Number(gap.duration || 0)) / 1000} s</b></span><br/>`
                    }
                    const err = findError(errorTimeline, x, gi)
                    if (err) html += `<span style="color:${CHART_COLORS.error}">● Sensor error value: <b>${err.value}</b></span><br/>`
                    return html
                }
            },
            toolbox: {
                right: 12,
                top: 4,
                feature: {
                    dataZoom: { yAxisIndex: 'none', title: { zoom: 'Zoom', back: 'Reset zoom' } },
                    restore: { title: 'Restore' }
                }
            },
            dataZoom: [
                { type: 'inside', throttle: 50 },
                { type: 'slider', height: 22, bottom: 40, borderColor: palette.grid, backgroundColor: 'transparent', dataBackground: { lineStyle: { color: CHART_COLORS.fuel }, areaStyle: { color: CHART_COLORS.fuel, opacity: 0.15 } } }
            ],
            xAxis: {
                type: 'time',
                axisLine: { lineStyle: { color: palette.axisLine } },
                axisLabel: { color: palette.muted, hideOverlap: true },
                splitLine: { show: false }
            },
            yAxis,
            series
        }
    }, [derived, stateBands, processedData, iotData, groupingInterval, showRawSensorData, showDataGaps, consumedData, data_gaps, errorTimeline, palette])

    buildOptionRef.current = buildOption

    // ------------------------------------------------------------------
    // Init the chart once; keep it across option rebuilds.
    // ------------------------------------------------------------------
    useEffect(() => {
        if (!isDataReady || !containerRef.current) return
        const chart = echarts.init(containerRef.current, null, { renderer: 'canvas' })
        chartRef.current = chart

        // Keep legend selection in a ref + relayout axes when a legend item toggles.
        chart.on('legendselectchanged', (params) => {
            legendStateRef.current = params.selected
            if (buildOptionRef.current) {
                chart.setOption(buildOptionRef.current(), { notMerge: true, lazyUpdate: true })
            }
        })

        const ro = new ResizeObserver(() => chart.resize())
        ro.observe(containerRef.current)

        return () => {
            ro.disconnect()
            chart.off('legendselectchanged')
            chart.dispose()
            chartRef.current = null
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDataReady])

    // Rebuild option whenever the derived data / toggles / theme change.
    useEffect(() => {
        const chart = chartRef.current
        if (!chart || !isDataReady) return
        setIsChartLoading(true)
        const raf = requestAnimationFrame(() => {
            chart.setOption(buildOption(), { notMerge: true, lazyUpdate: true })
            setIsChartLoading(false)
        })
        return () => cancelAnimationFrame(raf)
    }, [buildOption, isDataReady])

    const handleGroupingChange = useCallback((value) => {
        setSelectedGrouping(value)
        setIsChartLoading(true)
    }, [])

    const handleShowDataGapsChange = useCallback((e) => setShowDataGaps(e.target.checked), [])
    const handleShowRawSensorDataChange = useCallback((e) => setShowRawSensorData(e.target.checked), [])

    // Single "Download" menu for every export option.
    const handleDownload = useCallback(({ key }) => {
        const chart = chartRef.current
        switch (key) {
            case 'png':
            case 'jpg': {
                if (!chart) return
                const url = chart.getDataURL({ type: key, pixelRatio: 2, backgroundColor: palette.background })
                const a = document.createElement('a')
                a.href = url
                a.download = `range_data_chart.${key}`
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                break
            }
            case 'csv-processed':
                downloadBlob(toCSV(processedData), 'text/csv;charset=utf-8;', 'range_data_chart_processed.csv')
                break
            case 'csv-raw':
                downloadBlob(toCSV([...(iotData || [])].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))), 'text/csv;charset=utf-8;', 'range_data_chart_raw.csv')
                break
            case 'json-processed':
                downloadBlob(JSON.stringify(processedData, null, 2), 'application/json', 'range_data_chart_processed.json')
                break
            case 'json-raw':
                downloadBlob(JSON.stringify([...(iotData || [])].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)), null, 2), 'application/json', 'range_data_chart_raw.json')
                break
            default:
                break
        }
    }, [processedData, iotData, palette])

    const downloadMenu = (
        <Menu onClick={handleDownload}>
            <Menu.Item key="png"><Icon type="file-image" /> PNG Image</Menu.Item>
            <Menu.Item key="jpg"><Icon type="picture" /> JPG Image</Menu.Item>
            <Menu.Divider />
            <Menu.Item key="json-processed"><Icon type="file-text" /> JSON (Processed)</Menu.Item>
            <Menu.Item key="json-raw"><Icon type="file-text" /> JSON (Raw)</Menu.Item>
            <Menu.Divider />
            <Menu.Item key="csv-processed"><Icon type="file-excel" /> CSV (Processed)</Menu.Item>
            <Menu.Item key="csv-raw"><Icon type="file-excel" /> CSV (Raw)</Menu.Item>
        </Menu>
    )

    if (!isDataReady) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: palette.muted }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ marginBottom: 10 }}>Loading data...</div>
                    <div style={{ width: 40, height: 40, border: `4px solid ${palette.spinnerTrack}`, borderTop: `4px solid ${palette.spinnerHead}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
                </div>
            </div>
        )
    }

    return (
        <div className="device-range-chart" style={{ width: '100%', height: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Select
                        style={{ width: 200 }}
                        value={selectedGrouping}
                        onChange={handleGroupingChange}
                        getPopupContainer={() => document.body}
                        dropdownStyle={{ zIndex: 1150 }}
                    >
                        {(needAggregateData ? groupingOptions : smallGroupingItems).map((option) => (
                            <Option key={option.value} value={option.value}>{option.label}</Option>
                        ))}
                    </Select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Checkbox checked={showDataGaps} onChange={handleShowDataGapsChange}>Show Data Gaps</Checkbox>
                    <Checkbox checked={showRawSensorData} onChange={handleShowRawSensorDataChange}>Show Raw Sensor Data</Checkbox>
                    <Dropdown
                        overlay={downloadMenu}
                        trigger={['click']}
                        getPopupContainer={() => document.body}
                        overlayStyle={{ zIndex: 1150 }}
                    >
                        <Tooltip title="Download" getPopupContainer={() => document.body} overlayStyle={{ zIndex: 1160 }}>
                            <Button size="small" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px', borderColor: '#12B886' }}>
                                <Icon type="cloud-download" style={{ fontSize: 18, color: '#12B886' }} />
                            </Button>
                        </Tooltip>
                    </Dropdown>
                    <div style={{ fontSize: 16, fontWeight: 'bold', color: palette.text }}>{title}</div>
                </div>
            </div>

            <div style={{ position: 'relative', width: '100%', height: '95%' }}>
                {isChartLoading && (
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: isDarkMode ? 'rgba(38,48,58,0.6)' : 'rgba(255,255,255,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, pointerEvents: 'none' }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ marginBottom: 10, fontSize: 14, color: palette.muted }}>Rendering chart...</div>
                            <div style={{ width: 30, height: 30, border: `3px solid ${palette.spinnerTrack}`, borderTop: `3px solid ${palette.spinnerHead}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
                        </div>
                    </div>
                )}
                <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
            </div>
        </div>
    )
}

export default React.memo(RangeDataChartV2)
